import { c as _c } from "react/compiler-runtime";
import React from "react";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import Link from "next/link";
import Image from "next/image";
import UserCICAndLevel, { UserCICAndLevelSize } from "@/components/user/utils/UserCICAndLevel";
import WinnerDropBadge from "@/components/waves/drops/winner/WinnerDropBadge";
import WaveDropTime from "@/components/waves/drops/time/WaveDropTime";
import UserProfileTooltipWrapper from "@/components/utils/tooltip/UserProfileTooltipWrapper";
import { resolveIpfsUrlSync } from "@/components/ipfs/IPFSContext";
import ApprovalStatusBadge from "@/components/waves/approval/ApprovalStatusBadge";
import { isOfficiallyApprovedDrop } from "@/helpers/waves/approve-wave.helpers";
interface WaveLeaderboardDropAuthorProps {
  readonly drop: ExtendedDrop;
  readonly showAvatar?: boolean | undefined;
  readonly winningThreshold?: number | null | undefined;
}
interface WaveLeaderboardDropAuthorAvatarProps {
  readonly drop: ExtendedDrop;
}
export const WaveLeaderboardDropAuthorAvatar: React.FC<WaveLeaderboardDropAuthorAvatarProps> = t0 => {
  const $ = _c(5);
  const {
    drop
  } = t0;
  const authorLabel = drop.author.handle ?? drop.author.primary_address;
  const t1 = `/${authorLabel}`;
  let t2;
  if ($[0] !== drop.author.pfp) {
    t2 = <div className="tw-relative tw-h-10 tw-w-10 tw-flex-shrink-0 tw-rounded-lg tw-bg-iron-900">{drop.author.pfp ? <div className="tw-h-full tw-w-full tw-rounded-lg"><div className="tw-h-full tw-w-full tw-max-w-full tw-overflow-hidden tw-rounded-lg tw-bg-iron-900 tw-ring-1 tw-ring-white/10"><div className="tw-flex tw-h-full tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-lg tw-text-center"><Image src={resolveIpfsUrlSync(drop.author.pfp)} alt="Profile picture" width={40} height={40} className="tw-rounded-lg tw-object-contain" /></div></div></div> : <div className="tw-h-full tw-w-full tw-rounded-lg tw-bg-iron-900 tw-ring-1 tw-ring-inset tw-ring-white/10" />}</div>;
    $[0] = drop.author.pfp;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== t1 || $[3] !== t2) {
    t3 = <Link href={t1} onClick={_temp} className="group tw-flex tw-items-center tw-gap-x-2 tw-no-underline">{t2}</Link>;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
};
export const WaveLeaderboardDropAuthor: React.FC<WaveLeaderboardDropAuthorProps> = t0 => {
  const $ = _c(30);
  const {
    drop,
    showAvatar: t1,
    winningThreshold
  } = t0;
  const showAvatar = t1 === undefined ? true : t1;
  const authorLabel = drop.author.handle ?? drop.author.primary_address;
  const isApproveDrop = typeof winningThreshold === "number" && winningThreshold > 0;
  const isApprovedDrop = isApproveDrop && isOfficiallyApprovedDrop(drop);
  let authorStatusBadge = null;
  if (isApprovedDrop) {
    const t2 = drop.winning_context?.decision_time ?? null;
    const t3 = drop.winning_context?.place ?? drop.rank;
    let t4;
    if ($[0] !== t2 || $[1] !== t3) {
      t4 = <ApprovalStatusBadge approvedAt={t2} order={t3} />;
      $[0] = t2;
      $[1] = t3;
      $[2] = t4;
    } else {
      t4 = $[2];
    }
    authorStatusBadge = t4;
  } else {
    if (!isApproveDrop) {
      const t2 = drop.winning_context?.decision_time ?? null;
      let t3;
      if ($[3] !== drop.rank || $[4] !== t2) {
        t3 = <WinnerDropBadge rank={drop.rank} decisionTime={t2} />;
        $[3] = drop.rank;
        $[4] = t2;
        $[5] = t3;
      } else {
        t3 = $[5];
      }
      authorStatusBadge = t3;
    }
  }
  let t2;
  if ($[6] !== drop || $[7] !== showAvatar) {
    t2 = showAvatar && <WaveLeaderboardDropAuthorAvatar drop={drop} />;
    $[6] = drop;
    $[7] = showAvatar;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const t3 = `/${authorLabel}`;
  let t4;
  if ($[9] !== authorLabel) {
    t4 = <span className="tw-mb-0 tw-text-md tw-font-semibold tw-leading-none">{authorLabel}</span>;
    $[9] = authorLabel;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t3 || $[12] !== t4) {
    t5 = <Link href={t3} onClick={_temp2} className="tw-no-underline">{t4}</Link>;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== authorLabel || $[15] !== t5) {
    t6 = <UserProfileTooltipWrapper user={authorLabel}>{t5}</UserProfileTooltipWrapper>;
    $[14] = authorLabel;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== drop.author.level) {
    t7 = <UserCICAndLevel level={drop.author.level} size={UserCICAndLevelSize.SMALL} />;
    $[17] = drop.author.level;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <div className="tw-size-[3px] tw-flex-shrink-0 tw-rounded-full tw-bg-iron-900" />;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== drop.created_at) {
    t9 = <WaveDropTime timestamp={drop.created_at} />;
    $[20] = drop.created_at;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== authorStatusBadge || $[23] !== t6 || $[24] !== t7 || $[25] !== t9) {
    t10 = <div className="tw-flex tw-min-w-0 tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1">{t6}{t7}{authorStatusBadge}{t8}{t9}</div>;
    $[22] = authorStatusBadge;
    $[23] = t6;
    $[24] = t7;
    $[25] = t9;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== t10 || $[28] !== t2) {
    t11 = <div className="tw-flex tw-min-w-0 tw-items-center tw-gap-x-3">{t2}{t10}</div>;
    $[27] = t10;
    $[28] = t2;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  return t11;
};
function _temp(e) {
  return e.stopPropagation();
}
function _temp2(e) {
  return e.stopPropagation();
}